import {
  Controller,
  FormProvider,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ACCOUNT_FIELD_CONFIGS, AccountFieldInput } from '@hcs/authn';
import { useAccount } from '@hcs/authn';
import {
  Button,
  Flex,
  Input,
  LoadingSpinner,
  StateDropdown,
} from '@hcs/design-system';
import { Account, AccountFields } from '@hcs/types';

import { useUserContactInfoPatch } from '../../hooks/useUserContactInfoPatch';
import {
  UserContactInfoFormData,
  UseUserContactInfoPatchArg,
} from '../../types/ContactInfo.types';
import { ChangeUserEmailLauncher } from '../ChangeUserEmail';

import styles from './UserContactInfoForm.module.css';

const FORM_SCHEMA = yup.object().shape({
  [AccountFields.FirstName]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.FirstName].validator,
  [AccountFields.LastName]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.LastName].validator,
  [AccountFields.Email]: ACCOUNT_FIELD_CONFIGS[AccountFields.Email].validator,
  [AccountFields.StreetAddress]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.StreetAddress].validator,
  [AccountFields.City]: ACCOUNT_FIELD_CONFIGS[AccountFields.City].validator,
  [AccountFields.State]: ACCOUNT_FIELD_CONFIGS[AccountFields.State].validator,
  [AccountFields.Zipcode]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.Zipcode].validator,
  [AccountFields.Phone]: ACCOUNT_FIELD_CONFIGS[AccountFields.Phone].validator,
});

interface Props {
  className?: string;
  emailConfirmationUrl: string;
}

const dataHcName = 'user-contact-info-form';

const Field = ({
  fieldName,
  form,
}: {
  fieldName: keyof UserContactInfoFormData;
  form: UseFormReturn<UserContactInfoFormData>;
}) => {
  const fieldConfig = ACCOUNT_FIELD_CONFIGS[fieldName];
  const errorMessage = form.formState.errors[fieldName]?.message;
  return (
    <Controller
      name={fieldName}
      control={form.control}
      render={({ field }) => {
        if (fieldName === AccountFields.State) {
          return (
            <StateDropdown
              {...field}
              value={field.value || ''}
              className={styles.Input}
              dataHcName={`${dataHcName}-${fieldName}`}
              placeholder={fieldConfig.label}
              onSelect={field.onChange}
            />
          );
        } else {
          return (
            <AccountFieldInput
              {...field}
              className={styles.Input}
              disabled={fieldName === AccountFields.Email}
              error={errorMessage}
            />
          );
        }
      }}
    />
  );
};

const convertUserContactInfoToFormData = (
  userContactInfo: Account['user'],
): UserContactInfoFormData => {
  return {
    [AccountFields.Email]: userContactInfo.email || '',
    [AccountFields.FirstName]: userContactInfo.firstName || '',
    [AccountFields.LastName]: userContactInfo.lastName || '',
    [AccountFields.StreetAddress]: userContactInfo.streetAddress || '',
    [AccountFields.City]: userContactInfo.city || '',
    [AccountFields.State]: userContactInfo.state || '',
    [AccountFields.Zipcode]: userContactInfo.zipCode || '',
    [AccountFields.Phone]: userContactInfo.phone || '',
  };
};

// this component ensures the account is loaded to populate w/ initial values
const UserContactInfoFormContent = ({
  className,
  userContactInfo,
  userId,
  emailConfirmationUrl,
}: Props & { userContactInfo: Account['user']; userId: number }) => {
  const { mutate: patchUserContactInfo } = useUserContactInfoPatch();
  // Create a form with the user contact info
  const form = useForm<UserContactInfoFormData>({
    resolver: yupResolver(FORM_SCHEMA),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: convertUserContactInfoToFormData(userContactInfo),
  });
  const handleSubmit = (formPayload: UserContactInfoFormData) => {
    // Only send dirty fields to the patch Api method
    const patchPayload: UseUserContactInfoPatchArg = { userId };
    Object.keys(form.formState.dirtyFields).forEach((f) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const field = f as keyof UserContactInfoFormData;
      if (form.formState.dirtyFields[field] && field !== AccountFields.Email) {
        const value = formPayload[field];
        // The BE will ignore null values, so use empty string to represent clearing a field
        patchPayload[field] = value === null ? '' : value;
      }
    });
    if (Object.keys(patchPayload).length) {
      patchUserContactInfo(patchPayload, {
        onSuccess: () => {
          form.reset(formPayload);
        },
      });
    }
  };
  return (
    <FormProvider {...form}>
      <form
        data-hc-name={dataHcName}
        className={className}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Field fieldName={AccountFields.FirstName} form={form} />
        <Field fieldName={AccountFields.LastName} form={form} />
        <Flex fullWidth className={styles.Input}>
          <Input
            dataHcName={`${dataHcName}-email-input`}
            value={userContactInfo.email}
            className={styles.EmailInput}
            onChange={() => {
              // noop
            }}
            disabled
          />
          <ChangeUserEmailLauncher confirmationUrl={emailConfirmationUrl} />
        </Flex>
        <Field fieldName={AccountFields.StreetAddress} form={form} />
        <Field fieldName={AccountFields.City} form={form} />
        <Field fieldName={AccountFields.State} form={form} />
        <Field fieldName={AccountFields.Zipcode} form={form} />
        <Field fieldName={AccountFields.Phone} form={form} />
        <Button
          dataHcName={`${dataHcName}-submit`}
          type="submit"
          label="Submit"
          disabled={!form.formState.isDirty || !form.formState.isValid}
        />
      </form>
    </FormProvider>
  );
};

export const UserContactInfoForm = (props: Props) => {
  const { data: account } = useAccount();
  if (!account) {
    return (
      <LoadingSpinner absoluteCenter dataHcName={`${dataHcName}-skeleton`} />
    );
  } else {
    return (
      <UserContactInfoFormContent
        {...props}
        userId={account.user.id}
        userContactInfo={account.user}
      />
    );
  }
};
